'use client';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronRight, ArrowLeft, Stethoscope, Calendar, Gift } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { cn } from '@/utils/cn';

const navLinks = [
  { label: 'Especialidades', href: '/#especialidades' },
  { label: 'Plantão 24h', href: '/#plantao', isLive: true },
  { label: 'Médico em Casa', href: '/#domiciliar' },
  { label: 'Como Funciona', href: '/#como-funciona' },
  { label: 'Indique e Ganhe', href: '/#indique', isReferral: true },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 15);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 w-full transition-all duration-300 border-b',
          isScrolled
            ? 'h-16 lg:h-18 bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl shadow-xs border-slate-200/80 dark:border-white/[0.08]'
            : 'h-18 lg:h-22 bg-white/90 dark:bg-slate-950/90 backdrop-blur-lg border-slate-200/50 dark:border-white/[0.05]'
        )}
      >
        <div className="container-landing h-full">
          <div className="flex items-center justify-between h-full gap-4 lg:gap-8">
            {/* Logo & Optional Home Shortcut */}
            <div className="flex items-center gap-3 shrink-0">
              <Link
                to="/"
                className="flex items-center group transition-transform duration-200 hover:scale-[1.02]"
                aria-label="Medikei Página Inicial"
              >
                <img
                  src="/logo.png"
                  alt="Medikei"
                  className={cn(
                    'w-auto drop-shadow-xs dark:brightness-110 transition-all duration-300',
                    isScrolled ? 'h-8 sm:h-9' : 'h-8 sm:h-9 lg:h-10'
                  )}
                  fetchPriority="high"
                  decoding="async"
                />
              </Link>
            </div>

            {/* Desktop Navigation Links - Clean Reorganized Navigation */}
            <nav className="hidden lg:flex items-center gap-1 bg-transparent px-1 py-1">
              {navLinks.map((link) => {
                if (link.isReferral) {
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      className="group relative inline-flex items-center justify-center p-[1.5px] rounded-full overflow-visible transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] shadow-xs hover:shadow-amber-500/25 ml-1"
                    >
                      {/* Borda dourada animada correndo de um lado para o outro */}
                      <span className="absolute inset-0 rounded-full gold-animated-border" />

                      {/* Miolo do botão / Pílula com texto preto */}
                      <span className="relative z-10 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-bold bg-white dark:bg-slate-950 text-black dark:text-white group-hover:bg-slate-50 dark:group-hover:bg-slate-900 transition-colors duration-200">
                        <span>{link.label}</span>
                      </span>

                      {/* Ícone de presente na borda inferior direita aumentado */}
                      <span
                        className="absolute -bottom-1.5 -right-1.5 z-20 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-gradient-to-tr from-amber-600 via-amber-500 to-yellow-400 text-white shadow-sm ring-2 ring-white dark:ring-slate-950 transition-transform duration-200 group-hover:scale-115 group-hover:rotate-12"
                        title="Programa Indique e Ganhe"
                      >
                        <Gift className="w-3.5 h-3.5 text-white" />
                      </span>
                    </a>
                  );
                }

                return (
                  <a
                    key={link.label}
                    href={link.href}
                    className="group relative flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100/80 dark:hover:bg-white/10 transition-all duration-200"
                  >
                    {link.isLive && (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                      </span>
                    )}
                    <span>{link.label}</span>
                  </a>
                );
              })}
            </nav>

            {/* Desktop User Actions - Generously Spaced */}
            <div className="hidden md:flex items-center gap-3 lg:gap-3.5 shrink-0">
              <ThemeToggle />

              <Link
                to="/para-medicos"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full text-primary bg-primary/10 hover:bg-primary/20 border border-primary/25 transition-all duration-200 shadow-2xs hover:scale-[1.02] hover:-translate-y-0.5 cursor-pointer"
                title="Área exclusiva para médicos e profissionais de saúde"
              >
                <Stethoscope className="w-3.5 h-3.5" />
                <span>Sou Médico</span>
              </Link>

              <div className="h-5 w-px bg-border/80 dark:bg-white/10 mx-0.5" />

              <Link
                to="/login"
                className="text-xs lg:text-sm font-semibold text-foreground/80 hover:text-primary transition-colors px-3 py-2 rounded-full hover:bg-muted/50"
              >
                Entrar
              </Link>

              <a href="#especialidades">
                <Button
                  size="sm"
                  className="rounded-full font-bold bg-gradient-to-r from-blue-600 via-primary to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/35 transition-all hover:scale-[1.02] hover:-translate-y-0.5 active:scale-98 gap-1.5 h-9 lg:h-10 px-5"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Agendar Consulta</span>
                </Button>
              </a>
            </div>

            {/* Mobile Navigation Trigger */}
            <div className="flex items-center gap-2 md:hidden">
              <ThemeToggle />
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 text-foreground hover:text-primary transition-colors rounded-xl border border-border/60 bg-muted/30"
                aria-label="Abrir menu de navegação"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 z-[101] bg-card text-card-foreground w-full max-w-xs border-l border-border flex flex-col p-6 shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <Link
                to="/"
                className="flex items-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <img src="/logo.png" alt="Medikei" className="h-8 w-auto dark:brightness-110" decoding="async" />
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-foreground hover:bg-muted rounded-lg transition-colors"
                aria-label="Fechar menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-6 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-3 rounded-xl text-base font-semibold text-primary hover:bg-muted transition-colors"
              >
                <span className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Página Inicial
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Link>

              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center justify-between px-3 py-3 rounded-xl text-base transition-colors",
                    link.isReferral
                      ? "font-bold text-black dark:text-white bg-amber-50/70 dark:bg-amber-950/30 border border-amber-300/60 dark:border-amber-700/50"
                      : "font-medium text-foreground hover:bg-muted"
                  )}
                >
                  <span className="flex items-center gap-2">
                    {link.isLive && (
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    )}
                    {link.isReferral && (
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-tr from-amber-600 via-amber-500 to-yellow-400 text-white shadow-2xs">
                        <Gift className="w-3.5 h-3.5 text-white" />
                      </span>
                    )}
                    {link.label}
                  </span>
                  <ChevronRight className={cn("w-4 h-4", link.isReferral ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground")} />
                </a>
              ))}
            </nav>

            <div className="pt-4 border-t border-border space-y-3">
              <Link
                to="/para-medicos"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-bold text-primary bg-primary/10 border border-primary/30 rounded-xl hover:bg-primary/20 transition-colors"
              >
                <Stethoscope className="w-4 h-4" />
                <span>Sou Médico (Área do Profissional)</span>
              </Link>
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-center py-2.5 text-sm font-semibold text-foreground border border-border rounded-xl hover:border-primary hover:text-primary transition-colors"
              >
                Entrar
              </Link>
              <Link to="/cadastro" onClick={() => setIsMobileMenuOpen(false)}>
                <Button variant="primary" className="w-full rounded-xl font-bold bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white shadow-md shadow-primary/25">
                  Criar conta de Paciente
                </Button>
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
}
